import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../components/DashboardLayout";
import { serviceAPI } from "../services/api";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function ServicesPage() {
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.Auth);

    const [profileData, setProfileData] = useState({});
    const [formData, setFormData] = useState({
        services: [],
    });

    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [newService, setNewService] = useState({
        type: "",
        customType: "",
        price: "",
        currency: "INR",
        duration: "",
        durationUnit: "session",
        description: "",
        features: [],
    });

    const serviceTypes = [
        { id: "1-on-1", name: "1:1 Personal Training", icon: "bi-person" },
        { id: "online", name: "Online Training", icon: "bi-laptop" },
        { id: "diet", name: "Diet Consultation", icon: "bi-apple" },
        { id: "transformation", name: "Monthly Transformation", icon: "bi-graph-up-arrow" },
        { id: "group", name: "Group Training", icon: "bi-people" },
        { id: "custom", name: "Custom Program", icon: "bi-gear" },
    ];

    const currencies = [
        { code: "INR", symbol: "₹", name: "Indian Rupee" },
        { code: "USD", symbol: "$", name: "US Dollar" },
        { code: "EUR", symbol: "€", name: "Euro" },
        { code: "GBP", symbol: "£", name: "British Pound" },
        { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
    ];

    const durationUnits = [
        { value: "session", label: "Per Session" },
        { value: "week", label: "Per Week" },
        { value: "month", label: "Per Month" },
        { value: "package", label: "Package" },
    ];

    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (!storedUser && !isAuthenticated) {
            navigate("/login");
            return;
        }

        // Fetch services data from API
        const fetchServicesData = async () => {
            try {
                setFetchingData(true);
                const response = await serviceAPI.getAll();

                if (response.data.success) {
                    const data = response.data.data;
                    setFormData({
                        services: data.services || [],
                    });
                }
            } catch (error) {
                console.error("Error fetching services data:", error);
                if (error.response?.status !== 404) {
                    toast.error("Failed to load services");
                }
            } finally {
                setFetchingData(false);
            }
        };

        fetchServicesData();
    }, [navigate, isAuthenticated]);

    const handleServiceInputChange = (e) => {
        const { name, value } = e.target;
        setNewService((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAddFeature = () => {
        const featureInput = document.getElementById("featureInput");
        const feature = featureInput.value.trim();

        if (feature) {
            setNewService((prev) => ({
                ...prev,
                features: [...prev.features, feature],
            }));
            featureInput.value = "";
        }
    };

    const handleRemoveFeature = (index) => {
        setNewService((prev) => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index),
        }));
    };

    const handleOpenServiceModal = (service = null) => {
        if (service) {
            // Editing existing service
            setEditingService(service);
            setNewService(service);
        } else {
            // Adding new service
            setEditingService(null);
            setNewService({
                type: "",
                customType: "",
                price: "",
                currency: "INR",
                duration: "",
                durationUnit: "session",
                description: "",
                features: [],
            });
        }
        setShowServiceModal(true);
    };

    const handleSaveService = () => {
        // Validation
        if (!newService.type) {
            toast.error("Please select a service type");
            return;
        }

        if (newService.type === "custom" && !newService.customType) {
            toast.error("Please enter custom service name");
            return;
        }

        if (!newService.price || parseFloat(newService.price) <= 0) {
            toast.error("Please enter a valid price");
            return;
        }

        if (!newService.duration) {
            toast.error("Please enter duration");
            return;
        }

        if (!newService.description) {
            toast.error("Please enter service description");
            return;
        }

        const service = {
            id: editingService ? editingService.id : Date.now(),
            ...newService,
            displayName: newService.type === "custom"
                ? newService.customType
                : serviceTypes.find(t => t.id === newService.type)?.name,
        };

        if (editingService) {
            // Update existing service
            setFormData((prev) => ({
                ...prev,
                services: prev.services.map((s) => (s.id === service.id ? service : s)),
            }));
            toast.success("Service updated successfully!");
        } else {
            // Add new service
            setFormData((prev) => ({
                ...prev,
                services: [...prev.services, service],
            }));
            toast.success("Service added successfully!");
        }

        setShowServiceModal(false);
        setEditingService(null);
    };

    const handleDeleteService = (serviceId) => {
        if (window.confirm("Are you sure you want to delete this service?")) {
            setFormData((prev) => ({
                ...prev,
                services: prev.services.filter((s) => s.id !== serviceId),
            }));
            toast.success("Service deleted successfully!");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (formData.services.length === 0) {
            toast.error("Please add at least one service");
            return;
        }

        setLoading(true);

        try {
            // Call API to update services
            const response = await serviceAPI.updateAll(formData);

            if (response.data.success) {
                toast.success("Services updated successfully!");

                // Update form data with response
                setFormData({
                    services: response.data.data.services || [],
                });
            } else {
                toast.error(response.data.message || "Failed to update services");
            }
        } catch (error) {
            console.error("Error updating services:", error);
            toast.error(
                error.response?.data?.message ||
                "Failed to update services. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const getCompletionPercentage = () => {
        return formData.services.length > 0 ? 100 : 0;
    };

    const completionPercentage = getCompletionPercentage();

    const getCurrencySymbol = (code) => {
        return currencies.find((c) => c.code === code)?.symbol || code;
    };

    return (
        <DashboardLayout activeTab="services">
            <div className="services-tab">
                <div className="tab-header">
                    <div>
                        <h3>
                            <i className="bi bi-cash-stack me-2"></i>
                            Services & Pricing
                        </h3>
                        <p className="text-muted">Define your training services and pricing structure</p>
                    </div>
                    <div className="completion-badge">
                        <div className="mini-progress-circle">
                            <svg width="60" height="60">
                                <circle cx="30" cy="30" r="25" fill="none" stroke="#e9ecef" strokeWidth="5" />
                                <circle
                                    cx="30"
                                    cy="30"
                                    r="25"
                                    fill="none"
                                    stroke="#cc0000"
                                    strokeWidth="5"
                                    strokeDasharray={`${(completionPercentage / 100) * 157} 157`}
                                    transform="rotate(-90 30 30)"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="mini-percentage">{completionPercentage}%</div>
                        </div>
                    </div>
                </div>

                {fetchingData ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-danger" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mt-3">Loading services...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="profile-form">
                        {/* Services Section */}
                        <div className="form-section">
                            <div className="section-header-with-action">
                                <div>
                                    <h5 className="section-title">
                                        <i className="bi bi-list-check me-2"></i>
                                        Your Services <span className="text-danger">*</span>
                                    </h5>
                                    <p className="text-muted">Add and manage your training services</p>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => handleOpenServiceModal()}
                                >
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Add Service
                                </button>
                            </div>

                            {formData.services.length > 0 ? (
                                <div className="services-grid">
                                    {formData.services.map((service) => (
                                        <div key={service.id} className="service-card">
                                            <div className="service-header">
                                                <div className="service-type-badge">
                                                    {service.displayName}
                                                </div>
                                                <div className="service-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => handleOpenServiceModal(service)}
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDeleteService(service.id)}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="service-price">
                                                <span className="price-amount">
                                                    {getCurrencySymbol(service.currency)}{service.price}
                                                </span>
                                                <span className="price-duration">
                                                    / {service.duration} {service.durationUnit}
                                                </span>
                                            </div>

                                            <div
                                                className="service-description"
                                                dangerouslySetInnerHTML={{ __html: service.description }}
                                            />

                                            {service.features && service.features.length > 0 && (
                                                <div className="service-features">
                                                    <strong>Includes:</strong>
                                                    <ul>
                                                        {service.features.map((feature, index) => (
                                                            <li key={index}>
                                                                <i className="bi bi-check-circle-fill text-success me-2"></i>
                                                                {feature}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <i className="bi bi-cash-coin" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                                    <p className="text-muted mt-2">No services added yet</p>
                                    <p className="text-muted">Click "Add Service" to create your first service</p>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="form-actions">
                            <button
                                type="submit"
                                className="btn btn-danger btn-lg"
                                disabled={loading || formData.services.length === 0}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle me-2"></i>
                                        Save Services
                                    </>
                                )}
                            </button>
                            {formData.services.length === 0 && (
                                <p className="text-muted mt-2 mb-0">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Add at least one service to save
                                </p>
                            )}
                        </div>
                    </form>
                )}

                {/* Service Modal */}
                {showServiceModal && (
                    <div className="modal-overlay" onClick={() => setShowServiceModal(false)}>
                        <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h5>
                                    <i className="bi bi-cash-stack me-2"></i>
                                    {editingService ? "Edit Service" : "Add New Service"}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowServiceModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    {/* Service Type */}
                                    <div className="col-12">
                                        <label className="form-label">
                                            Service Type <span className="text-danger">*</span>
                                        </label>
                                        <div className="service-type-grid">
                                            {serviceTypes.map((type) => (
                                                <div
                                                    key={type.id}
                                                    className={`service-type-option ${newService.type === type.id ? "selected" : ""
                                                        }`}
                                                    onClick={() =>
                                                        setNewService((prev) => ({ ...prev, type: type.id }))
                                                    }
                                                >
                                                    <i className={type.icon}></i>
                                                    <span>{type.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Custom Service Name */}
                                    {newService.type === "custom" && (
                                        <div className="col-12">
                                            <label htmlFor="customType" className="form-label">
                                                Custom Service Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="customType"
                                                name="customType"
                                                value={newService.customType}
                                                onChange={handleServiceInputChange}
                                                placeholder="e.g., Yoga & Meditation Package"
                                            />
                                        </div>
                                    )}

                                    {/* Price */}
                                    <div className="col-md-6">
                                        <label htmlFor="price" className="form-label">
                                            Price <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <select
                                                className="form-select"
                                                name="currency"
                                                value={newService.currency}
                                                onChange={handleServiceInputChange}
                                                style={{ maxWidth: "120px" }}
                                            >
                                                {currencies.map((curr) => (
                                                    <option key={curr.code} value={curr.code}>
                                                        {curr.symbol} {curr.code}
                                                    </option>
                                                ))}
                                            </select>
                                            <input
                                                type="number"
                                                className="form-control"
                                                id="price"
                                                name="price"
                                                value={newService.price}
                                                onChange={handleServiceInputChange}
                                                placeholder="0.00"
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                    </div>

                                    {/* Duration */}
                                    <div className="col-md-6">
                                        <label htmlFor="duration" className="form-label">
                                            Duration <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="duration"
                                                name="duration"
                                                value={newService.duration}
                                                onChange={handleServiceInputChange}
                                                placeholder="e.g., 1, 3, 12"
                                            />
                                            <select
                                                className="form-select"
                                                name="durationUnit"
                                                value={newService.durationUnit}
                                                onChange={handleServiceInputChange}
                                            >
                                                {durationUnits.map((unit) => (
                                                    <option key={unit.value} value={unit.value}>
                                                        {unit.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="col-12">
                                        <label htmlFor="description" className="form-label">
                                            Description <span className="text-danger">*</span>
                                        </label>
                                        <ReactQuill
                                            theme="snow"
                                            value={newService.description}
                                            onChange={(content) => {
                                                setNewService((prev) => ({
                                                    ...prev,
                                                    description: content,
                                                }));
                                            }}
                                            modules={{
                                                toolbar: [
                                                    [{ 'header': [1, 2, 3, false] }],
                                                    ['bold', 'italic', 'underline'],
                                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                    ['link'],
                                                    ['clean']
                                                ],
                                            }}
                                            formats={[
                                                'header',
                                                'bold', 'italic', 'underline',
                                                'list', 'bullet',
                                                'link'
                                            ]}
                                            placeholder="Describe what's included in this service..."
                                            className="bio-editor"
                                        />
                                    </div>

                                    {/* Features */}
                                    <div className="col-12">
                                        <label className="form-label">Features / What's Included</label>
                                        <div className="input-group mb-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="featureInput"
                                                placeholder="e.g., Personalized workout plan"
                                                onKeyPress={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleAddFeature();
                                                    }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={handleAddFeature}
                                            >
                                                <i className="bi bi-plus"></i> Add
                                            </button>
                                        </div>
                                        {newService.features.length > 0 && (
                                            <div className="features-list">
                                                {newService.features.map((feature, index) => (
                                                    <div key={index} className="feature-item">
                                                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                                                        <span>{feature}</span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-link text-danger"
                                                            onClick={() => handleRemoveFeature(index)}
                                                        >
                                                            <i className="bi bi-x"></i>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowServiceModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-danger" onClick={handleSaveService}>
                                    <i className="bi bi-check-circle me-2"></i>
                                    {editingService ? "Update Service" : "Add Service"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
